import { useTranslation } from 'react-i18next';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import React from 'react';
import Page from 'Core/components/Page';
import Card from 'Core/components/FormElements/Card';
import { PrimaryButton } from 'Core/components/Button';
import { HiOutlineExclamationTriangle } from 'react-icons/hi2';
import PostExchangeBanner from 'Modules/financing/components/PostExchangeBanner';
import Loader from 'Core/components/Loader';
import useProviderSync from 'Modules/financing/hooks/useProviderSync';

export default function FinaningCorrectionRequired({ application }) {
  document.title = 'Payment Portal - Correction Required';
  useScrollTop();

  const { t } = useTranslation();
  // Only the refreshed application state matters here, not its links
  const { mutateAsync: syncProvider, isLoading: isProviderSyncLoading } = useProviderSync({
    isReady: (data) => Boolean(data?.next_action),
  });
  const navigateToVerificationLink = async (e) => {
    e.preventDefault();
    // Reload after the provider sync finishes or times out, so the page shows the latest state
    await syncProvider();
    window.location.reload();
  };

  return (
    <Page title={t('page.financing.header')} withStepProgress={false} stickyFooter>
      {isProviderSyncLoading && (
        <Loader message={t('financing.nextgen.correctionRequired.syncLoadingText')} />
      )}
      <div className="mx-auto max-w-4xl px-4 mt-10">
        <PostExchangeBanner application={application} />

        <Card className="relative overflow-hidden !px-0 !py-0" cardPadding="sm">
          <div className="h-1 bg-amber-500" />

          <div className="px-8 md:px-14 pt-12 pb-12">
            <div className="flex justify-center">
              <div
                aria-hidden="true"
                className="flex items-center justify-center h-14 w-14 rounded-full bg-amber-50 ring-1 ring-amber-100"
              >
                <HiOutlineExclamationTriangle className="h-7 w-7 text-amber-600" />
              </div>
            </div>

            <h1
              className="mt-6 text-center text-3xl md:text-[2rem] font-semibold tracking-tight text-slate-900 leading-tight"
              data-testid="title-correction-required"
            >
              {t('common.newApplication')}
            </h1>

            <div aria-hidden="true" className="mt-7 h-[3px] w-full rounded-full bg-amber-300" />

            <div
              className="mt-7 mx-auto max-w-2xl text-center whitespace-pre-line text-base md:text-lg leading-relaxed text-slate-600"
              data-testid="correction-required-message"
            >
              {t('synchrony.correctionRequired')}
            </div>

            <div className="mt-10 flex justify-center">
              <PrimaryButton
                data-testid="btn-verify"
                size="tall"
                rounded
                className="!px-12"
                onClick={navigateToVerificationLink}
                disabled={isProviderSyncLoading}
              >
                {t('common.retry')}
              </PrimaryButton>
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}
